import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { StartService } from '../services/start.service';
import { Router } from '@angular/router';
import { ToastService } from 'asksuite-citrus';
import { UserService } from '../services/user.service';
import { LocalizationService } from '../services/localization.service';
import { BFF_ERROR_CODES } from '../constants/bff-error-messages';
import { environment } from 'src/environments/environment';
import { SessionService } from '../services/session.service';
import { LoggerService } from '../services/logger.service';
import { LogEvent } from '../enums/log-event';
import { TokenRefreshSchedulerService } from '../services/token-refresh-scheduler.service';
import { ConnectionExpiredService } from '../services/connection-expired.service';

@Injectable()
export class UnauthorizedInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshDone$ = new BehaviorSubject<boolean>(false);

  private readonly refreshUrl = `${environment.externalUrls.bff}/api/auth/refreshToken`;

  constructor(
    private startService: StartService,
    private router: Router,
    private toaster: ToastService,
    private userService: UserService,
    private localizeService: LocalizationService,
    private location: Location,
    private sessionService: SessionService,
    private httpClient: HttpClient,
    private loggerService: LoggerService,
    private tokenRefreshScheduler: TokenRefreshSchedulerService,
    private connectionExpiredService: ConnectionExpiredService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status !== 401) {
          return throwError(() => err);
        }

        if (this.shouldRefreshToken(err, request)) {
          return this.handleRefreshAndRetry(request, next);
        }

        if (err.error?.code) {
          switch (err.error.code) {
            case BFF_ERROR_CODES.INVALID_COMPANIES:
              this.refreshCompanies();
              break;
            case BFF_ERROR_CODES.INVALID_CONNECTION_ID:
              this.triggerConnectionExpiredModal();
              break;
            case BFF_ERROR_CODES.INVALID_TOKEN:
              this.gotoPlatform();
              break;
            case BFF_ERROR_CODES.INVALID_TOKEN_REFRESH:
              this.sessionService.exitWithRecovery();
              break;
            case BFF_ERROR_CODES.NOT_FOUND_AUTHORIZATION_OR_COOKIE:
              this.loggerService.warn(LogEvent.LIVECHAT_NOT_FOUND_AUTH_OR_COOKIE);
              this.tokenRefreshScheduler.expireSession();
              break;
            case BFF_ERROR_CODES.SESSION_EXPIRED:
              this.loggerService.warn(LogEvent.LIVECHAT_SESSION_EXPIRED);
              this.tokenRefreshScheduler.expireSession();
              break;
            default:
              break;
          }
        }

        return throwError(() => err);
      })
    );
  }

  private handleRefreshAndRetry(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshDone$.next(false);

      return this.httpClient
        .post(this.refreshUrl, null, { withCredentials: true })
        .pipe(
          switchMap(() => {
            this.isRefreshing = false;
            this.refreshDone$.next(true);
            this.loggerService.info(LogEvent.LIVECHAT_JWT_INTERCEPTOR_REFRESH_SUCCESS);

            return next.handle(request);
          }),
          catchError((refreshError: HttpErrorResponse) => {
            this.isRefreshing = false;
            this.refreshDone$.next(true);

            if (
              refreshError.error?.code ===
              BFF_ERROR_CODES.INVALID_JWT_REFRESH_TOKEN
            ) {
              this.loggerService.warn(LogEvent.LIVECHAT_JWT_INTERCEPTOR_REFRESH_EXPIRED);
              this.sessionService.exit();
            }

            if (
              refreshError.error?.code ===
              BFF_ERROR_CODES.INVALID_REFRESH_TOKEN_COOKIE_NOT_FOUND
            ) {
              this.loggerService.warn(LogEvent.LIVECHAT_JWT_INTERCEPTOR_REFRESH_COOKIE_NOT_FOUND);
              this.sessionService.exit();
            }

            if (refreshError.error?.code === BFF_ERROR_CODES.SESSION_EXPIRED) {
              this.loggerService.warn(LogEvent.LIVECHAT_SESSION_EXPIRED);
              this.tokenRefreshScheduler.expireSession();
            }

            return throwError(() => refreshError);
          })
        );
    }

    return this.refreshDone$.pipe(
      filter((done) => done),
      take(1),
      switchMap(() => next.handle(request))
    );
  }

  private shouldRefreshToken(
    err: HttpErrorResponse,
    request: HttpRequest<unknown>
  ): boolean {
    return (
      err.error?.code === BFF_ERROR_CODES.INVALID_JWT_ACCESS_TOKEN &&
      !request.url.startsWith(this.refreshUrl)
    );
  }

  private refreshCompanies() {
    this.startService.prepareCompaniesAndUsers().subscribe({
      next: () => {
        this.refreshApplicationByCompanyUpdated();
      },
      error: () => {
        this.toaster.danger(
          this.localizeService.key('ERROR_WHILE_REFRESHING_COMPANIES')
        );
      },
    });
  }

  private gotoPlatform() {
    window.open(environment.externalUrls.platform, '_self');
  }

  private triggerConnectionExpiredModal() {
    this.connectionExpiredService.triggerConnectionExpired();
  }

  private refreshApplication() {
    if (!this.userService.isCurrentUserGlobal()) {
      this.router.navigate(['']);
      this.location.reload();
    }
  }

  private refreshApplicationByCompanyUpdated() {
    if (!this.userService.isCurrentUserGlobal()) {
      this.toaster.info(this.localizeService.key('COMPANIES_HAS_BEEN_UPDATED'));
      this.refreshApplication();
    }
  }
}
